import { clsx } from 'clsx';
import dayjs from 'dayjs';
import { AlertTriangle, CheckCircle, ClipboardList, Info, User2, XCircle } from 'lucide-react';
import mapboxgl from 'mapbox-gl';
import ReactDOMServer from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { StatusDot } from '@/components';
import { constants } from './constants';
import { Badge } from '@/components';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const capitalizeWords = word => {
  return word
    .split(' ')
    .map(char => char.charAt(0).toUpperCase() + char.slice(1))
    .join(' ');
};

export const decideBadgeVariantByRespondersStatus = status => {
  let variant;
  if (
    status === 'Unacknowledged' ||
    status === 'acknowledged' ||
    status === 'left base' ||
    status === 'arrive at scene' ||
    status === 'left scene' ||
    status === 'arrive at base'
  ) {
    variant = 'SoftBlue';
  } else if (status === 'available') {
    variant = 'SoftGreen';
  } else if (status === 'offline') {
    variant = 'SoftGray';
  }
  return variant;
};

export const FILE_TYPE_EXTENSIONS = {
  image: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'],
  video: ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'],
  audio: ['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a', 'webm'],
};

export const mapbox_accessToken = window?.wideBridgeConfig?.mapBox?.token || process.env.REACT_APP_MAP_BOX_ACCESS_TOKEN;

export const validationRules = {
  title: {
    regex: /^.{3,50}$/, // at least 3 chars, max 50
    message: 'Title must be between 3 and 50 characters',
  },
  description: {
    regex: /^(?!.*  )[\s\S]{10,500}$/,
    message: 'Description must be 10-500 characters and can include letters, numbers, and punctuation.',
  },
  location: {
    // Example: at least 3 characters, max 500, can include letters, numbers, spaces, commas, hyphens
    regex: /^[\p{L}\p{N}\s,.\-'()#/&°]+$/u,
    message: 'Location contains invalid characters',
  },
  location_description: {
    regex: /^[\p{L}\p{N}\s,.\-'()#/&°]+$/u,
    message: 'Location description contains invalid characters',
  },
};

// Security: Input length limits
export const INPUT_LIMITS = {
  title: 200,
  description: 1000,
  location: 500,
};

// Performance: Rate limiting for submit button
export const SUBMIT_DEBOUNCE_MS = 2000;

// Security: Sanitize string input to prevent XSS
export const sanitizeInput = input => {
  if (typeof input !== 'string') return input;
  return input
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/\s{2,}/g, ' ') // Collapse multiple spaces
    .slice(0, INPUT_LIMITS.description); // Enforce max length
};

export const getCurrentCoord = () => {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      position => {
        const lat = position.coords.latitude;
        const long = position.coords.longitude;
        const data = { lat, long };
        resolve(data);
      },
      error => reject(error)
    );
  });
};

export function setStatus(status) {
  const { t } = useTranslation();
  let variant;
  switch (status) {
    case 'created':
    case 'progress':
      variant = 'SurfaceRed';
      break;
    case 'resolved':
    case 'closed':
      variant = 'SurfaceGreen';
      break;
    default:
      variant = '';
  }
  return (
    <Badge variant={variant} size="sm" className="text-xs">
      {t('INCIDENT_CARD_STATUS_VALUES', { returnObjects: true }).find(item => item.value === status)?.label ||
        capitalizeWords(status)}
    </Badge>
  );
}

export function setSeverity(severity) {
  const { t } = useTranslation();
  let label, color;
  switch (severity) {
    case 0:
      label = capitalizeWords(constants.INCIDENTS_PANEL.SEVERITY_MINIMAL);
      color = 'bg-icon-muted';
      break;
    case 1:
      label = capitalizeWords(constants.INCIDENTS_PANEL.SEVERITY_LOW);
      color = 'bg-interactive';
      break;
    case 2:
      label = capitalizeWords(constants.INCIDENTS_PANEL.SEVERITY_MEDIUM);
      color = 'bg-chart-4';
      break;
    case 3:
      label = capitalizeWords(constants.INCIDENTS_PANEL.SEVERITY_HIGH);
      color = 'bg-chart-2';
      break;
    case 4:
      label = capitalizeWords(constants.INCIDENTS_PANEL.SEVERITY_CRITICAL);
      color = 'bg-icon-error';
      break;
  }

  return (
    <div className="flex items-center gap-2">
      <StatusDot className={color} />
      <span>{t('SEVERITY_VALUES', { returnObjects: true }).find(item => item.value === severity)?.label || label}</span>
    </div>
  );
}
export const getAddressFromCoord = async (lat, lng, mapbox_accessToken) => {
  try {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${mapbox_accessToken}`
    );
    const data = await response.json();
    // Take the first feature as most relevant
    return data.features?.[0]?.place_name || '';
  } catch (err) {
    console.error('Reverse geocoding failed', err);
    return '';
  }
};

// Function to get distance using Mapbox Matrix API
export const getDrivingDistance = async (origin, destination, mapbox_accessToken) => {
  if (!origin?.lat || !origin?.lon || !destination?.lat || !destination?.long) {
    return null;
  }
  const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${origin.lon},${origin.lat};${destination.long},${destination.lat}?access_token=${mapbox_accessToken}`;

  try {
    const response = await fetch(url);

    if (!response.ok) {
      return null;
    }

    const data = await response.json();

    if (data && data.routes && data.routes.length > 0) {
      return data.routes[0].distance;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};

export const updateRespondersDistance = async (responders, destination, mapbox_accessToken) => {
  try {
    if (!Array.isArray(responders) || responders.length === 0) {
      return [];
    }

    if (!destination?.lat || !destination?.long) {
      return responders.map(responder => ({ ...responder, distance: null }));
    }

    const updatedResponders = await Promise.all(
      responders?.map(async responder => {
        const origin = responder.lastKnownLocation;

        if (!origin?.lat || !origin?.lon) {
          return { ...responder, distance: null };
        }
        const distance = await getDrivingDistance(origin, destination, mapbox_accessToken);
        return {
          ...responder,
          distance,
        };
      })
    );

    updatedResponders.sort((a, b) => {
      if (a.distance == null) return 1;
      if (b.distance == null) return -1;
      return a.distance - b.distance;
    });

    return updatedResponders;
  } catch (error) {
    return [];
  }
};

export function formatDistance(distanceInMeters) {
  if (typeof distanceInMeters !== 'number' || isNaN(distanceInMeters)) {
    return '-';
  }

  if (distanceInMeters < 1000) {
    return `${Math.round(distanceInMeters)}m`;
  } else {
    return `${(distanceInMeters / 1000).toFixed(2)}km`;
  }
}

export const toastIcons = {
  success: <CheckCircle className="w-5 h-5" color="var(--color-icon-success)" />,
  error: <XCircle className="w-5 h-5" color="var(--color-icon-error)" />,
  info: <Info className="w-5 h-5" color="var(--color-icon-info)" />,
  warning: <AlertTriangle className="w-5 h-5" color="var(--color-border-warning-muted)" />,
  profile: <User2 className="w-5 h-5" color="var(--color-icon-primary)" />,
  incident: <ClipboardList className="w-5 h-5" color="var(--color-icon)" />,
};

// Function to get last message from chatBox inside msgObject
export const getLastMessage = msgObject => {
  console.log('msgObject', msgObject);
  if (!msgObject || !msgObject.messages) {
    console.log('No valid messages found');
    return null;
  }

  const messages = msgObject.messages;
  const lastMsg = messages[messages.length - 1];

  return lastMsg;
};

export const fetchRouteAndUpdateResponder = async ({
  responder,
  incidentLocation,
  isSelected = false,
  isHover = false,
}) => {
  if (!responder.lastKnownLocation?.lon || !responder.lastKnownLocation?.lat || !incidentLocation?.geoloc) {
    return { ...responder, isSelected, isHover };
  }

  const origin = `${responder.lastKnownLocation.lon},${responder.lastKnownLocation.lat}`;
  const destination = `${incidentLocation?.geoloc?.long},${incidentLocation?.geoloc?.lat}`;
  const profile = 'driving';

  const url = `https://api.mapbox.com/directions/v5/mapbox/${profile}/${origin};${destination}?geometries=geojson&access_token=${mapbox_accessToken}`;

  try {
    const res = await fetch(url);
    const data = await res.json();

    if (!data.routes || data.routes.length === 0) {
      console.warn(`No route found for responder ${responder.id}`);
      return { ...responder, isSelected, isHover };
    }

    return {
      ...responder,
      isSelected,
      isHover,
      route: data.routes[0].geometry,
    };
  } catch (error) {
    console.error(`Error fetching route for responder ${responder.id}:`, error);
    return { ...responder, isSelected, isHover };
  }
};

// utils/statusUtils.js
export const getResponderBadge = (rowData, statusIdToDisplayNameMap) => {
  let label = constants.UI.UNKNOWN;
  let variant = 'SolidPrimary';

  // Handle response_status first
  switch (rowData?.assignee?.response_status) {
    case constants?.RESPONDERS?.STATUS?.STATUS_KEY?.PENDING:
      label = constants?.RESPONDERS?.STATUS?.STATUS_VALUE.UNACKNOWLEDGED;
      variant = 'SurfaceBlue';
      return { label, variant };
    case constants?.RESPONDERS?.STATUS?.STATUS_KEY?.REJECTED:
      label = constants?.RESPONDERS?.STATUS?.STATUS_VALUE.REJECTED;
      variant = 'SurfaceRed';
      return { label, variant };
    case constants?.RESPONDERS?.STATUS?.STATUS_KEY?.ABORTED:
      label = constants?.RESPONDERS?.STATUS?.STATUS_VALUE.ABORTED;
      variant = 'SurfaceRed';
      return { label, variant };
    default:
      break;
  }

  // If no specific response_status, check latest status from statuses array
  if (rowData?.statuses?.length > 0) {
    const latestStatus = rowData.statuses[rowData.statuses.length - 1];
    label = statusIdToDisplayNameMap[latestStatus] || constants.UI.UNKNOWN;
    switch (label) {
      case constants?.RESPONDERS?.STATUS?.STATUS_KEY?.REACH_SITE:
        label = constants?.RESPONDERS?.STATUS?.STATUS_VALUE?.ARRIVED_AT_SCENE;
        variant = 'SurfaceBlue';
        break;
      case constants?.RESPONDERS?.STATUS?.STATUS_KEY?.ON_THE_WAY:
        label = constants?.RESPONDERS?.STATUS?.STATUS_VALUE?.ON_THE_WAY;
        variant = 'SurfaceBlue';
        break;
      case constants?.RESPONDERS?.STATUS?.STATUS_KEY?.DONE:
        label = constants?.RESPONDERS?.STATUS?.STATUS_KEY?.DONE;
        variant = 'SurfaceGreen';
        break;
      default:
        variant = 'SurfaceGray';
    }
  }

  return { label, variant };
};

export const getIncidentHash = incident => {
  return JSON.stringify({
    id: incident.id,
    title: incident.title,
    description: incident.description,
    type: incident.type,
    status: incident.status,
    severity: incident.severity,
    location: incident.location,
    assignees: incident.assignees,
    updated_at: incident.updated_at,
    lastUpdateTimestamp: incident.lastUpdateTimestamp,
  });
};

export const getNotificationDetails = (incident, isNew, previousIncident = null) => {
  if (isNew) {
    return {
      titleKey: 'UPDATES_PANEL.NEW_INCIDENT_TITLE',
      titleParams: { title: incident.title || incident.id },
      descriptionKey: 'UPDATES_PANEL.NEW_INCIDENT_DESCRIPTION',
      descriptionParams: {
        type: incident.type || 'default_type',
        location: incident.location?.display_name || 'unknown_location',
      },
    };
  }

  const changes = [];

  if (previousIncident) {
    if (previousIncident.status !== incident.status) {
      const statusKey =
        incident.status === 'resolved' ? 'UPDATES_PANEL.STATUS_CLOSED' : 'UPDATES_PANEL.STATUS_IN_PROGRESS';
      changes.push({ key: 'UPDATES_PANEL.STATUS_CHANGE', params: { status: statusKey } });
    }

    if (previousIncident.severity !== incident.severity) {
      changes.push({ key: 'UPDATES_PANEL.SEVERITY_CHANGE', params: { severity: incident.severity } });
    }

    if (previousIncident.type !== incident.type) {
      changes.push({ key: 'UPDATES_PANEL.TYPE_CHANGE', params: { type: incident.type } });
    }

    if (previousIncident.description !== incident.description) {
      changes.push({ key: 'UPDATES_PANEL.DESCRIPTION_UPDATED' });
    }

    if (JSON.stringify(previousIncident.assignees) !== JSON.stringify(incident.assignees)) {
      const prevCount = previousIncident.assignees?.length || 0;
      const newCount = incident.assignees?.length || 0;

      if (newCount > prevCount) {
        changes.push({ key: 'UPDATES_PANEL.RESPONDERS_ADDED', params: { count: newCount - prevCount } });
      } else if (newCount < prevCount) {
        changes.push({ key: 'UPDATES_PANEL.RESPONDERS_REMOVED', params: { count: prevCount - newCount } });
      } else {
        changes.push({ key: 'UPDATES_PANEL.RESPONDERS_UPDATED' });
      }
    }

    if (JSON.stringify(previousIncident.location) !== JSON.stringify(incident.location)) {
      changes.push({ key: 'UPDATES_PANEL.LOCATION_CHANGED' });
    }
  }

  return {
    titleKey: 'UPDATES_PANEL.UPDATE_TITLE',
    titleParams: { title: incident.title || incident.id },
    changes: changes.length > 0 ? changes : [{ key: 'UPDATES_PANEL.UPDATED_DEFAULT' }],
  };
};

export const useTranslateNotification = () => {
  const { t } = useTranslation();

  const translateTitle = title => {
    const [number, data] = title.split(' - ');
    const translatedText = t('INCIDENT_TYPES', { returnObjects: true }).find(item => item.value === data)?.label;
    return translatedText ? `${number} - ${translatedText}` : title;
  };

  const translateNotification = notification => {
    if (!notification.titleKey) {
      // Fallback for old notifications that already have translated text
      return {
        title: notification.title || '',
        description: notification.description || '',
      };
    }

    let title, description;

    // Translate title
    if (notification.titleKey === 'UPDATES_PANEL.NEW_INCIDENT_TITLE') {
      title = `${t(notification.titleKey)}: "${notification.titleParams.title}"`;
    } else if (notification.titleKey === 'UPDATES_PANEL.UPDATE_TITLE') {
      title = `${t(notification.titleKey)}: "${translateTitle(notification.titleParams.title)}"`;
    }

    // Translate description
    if (notification.descriptionKey) {
      // For new incidents
      const type =
        notification.descriptionParams.type === 'default_type'
          ? t('UPDATES_PANEL.DEFAULT_TYPE')
          : notification.descriptionParams.type;

      const location =
        notification.descriptionParams.location === 'unknown_location'
          ? t('UPDATES_PANEL.UNKNOWN_LOCATION')
          : notification.descriptionParams.location;

      description = `${type} ${t('UPDATES_PANEL.CREATED_AT')} ${location}`;
    } else if (notification.changes) {
      // For updated incidents
      const translatedChanges = notification.changes.map(change => {
        if (change.params?.status) {
          return `${t(change.key)}: ${t(change.params.status)}`;
        } else if (change.params?.severity) {
          return `${t(change.key)}: ${change.params.severity}`;
        } else if (change.params?.type) {
          return `${t(change.key)}: ${change.params.type}`;
        } else if (change.params?.count) {
          return `${change.params.count} ${t(change.key)}`;
        }
        return t(change.key);
      });

      description = translatedChanges.slice(0, 2).join(', ') + (translatedChanges.length > 2 ? '...' : '');
    }

    return { title, description };
  };

  return translateNotification;
};

export const formatRelativeTime = (timestamp, t) => {
  if (!timestamp) return t ? t('UPDATES_PANEL.UNKNOWN') : 'Unknown';

  const now = dayjs();

  // Handle both Unix timestamps (seconds) and milliseconds
  // If timestamp is less than 10000000000, it's likely in seconds
  const time =
    timestamp < 10000000000
      ? dayjs.unix(timestamp) // Unix timestamp in seconds
      : dayjs(timestamp); // Timestamp in milliseconds or ISO string

  const diffInSeconds = now.diff(time, 'second');

  // Just now (0-30 seconds)
  if (diffInSeconds < 30) {
    return t ? t('UPDATES_PANEL.JUST_NOW') : 'just now';
  }

  // Less than 1 minute (30-59 seconds)
  if (diffInSeconds < 60) {
    return t ? t('UPDATES_PANEL.JUST_NOW') : 'just now';
  }

  // Less than 1 hour
  if (diffInSeconds < 3600) {
    const mins = Math.floor(diffInSeconds / 60);
    return t ? t('UPDATES_PANEL.MINUTES_AGO', { count: mins }) : `${mins} min${mins > 1 ? 's' : ''} ago`;
  }

  // Less than 24 hours
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return t ? t('UPDATES_PANEL.HOURS_AGO', { count: hours }) : `${hours} hour${hours > 1 ? 's' : ''} ago`;
  }

  // Less than 7 days
  if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return t ? t('UPDATES_PANEL.DAYS_AGO', { count: days }) : `${days} day${days > 1 ? 's' : ''} ago`;
  }

  // More than 7 days, show actual date
  return time.format('MMM D, YYYY');
};

/**
 * Attempts to parse a string as GPS coordinates (lat, lng).
 * Accepts formats: "1.3521, 103.8198", "1.3521 103.8198", "1.3521,103.8198"
 * Returns { lat, lng } if valid, or null if not coordinates.
 */
export const parseGPSInput = input => {
  if (!input || typeof input !== 'string') return null;
  const trimmed = input.trim();
  // Match two numbers (at least one with a decimal point) separated by comma and/or whitespace
  const match = trimmed.match(/^(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)$/);
  if (!match) return null;
  // Require at least one decimal point to avoid matching plain integers like "123, 456"
  if (!match[1].includes('.') && !match[2].includes('.')) return null;
  const lat = parseFloat(match[1]);
  const lng = parseFloat(match[2]);
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
};

// Mapview Component
export const isValidCoordinates = coords => {
  if (!Array.isArray(coords) || coords.length !== 2) return false;
  const [lng, lat] = coords;
  return typeof lng === 'number' && typeof lat === 'number' && lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90;
};

export const fetchRoute = async (start, end, routeDataRef) => {
  if (!isValidCoordinates(start) || !isValidCoordinates(end)) return null;
  const cacheKey = `${start.join(',')}-${end.join(',')}`;
  if (routeDataRef.current.has(cacheKey)) return routeDataRef.current.get(cacheKey);

  try {
    const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${start.join(
      ','
    )};${end.join(',')}?geometries=geojson&access_token=${mapbox_accessToken}`;
    const res = await fetch(url);
    const data = await res.json();
    if (!data.routes?.length) return null;
    const geometry = data.routes[0].geometry;
    routeDataRef.current.set(cacheKey, geometry);
    return geometry;
  } catch (err) {
    console.error('Route fetch failed:', err);
    return null;
  }
};

// utils/mapUtils.js

export const createIncidentGeoJSON = (incidents, filter) => {
  const filtered = incidents.filter(i => i?.geoloc && isValidCoordinates([i.geoloc.long, i.geoloc.lat]));

  let features;

  switch (filter) {
    case 'active':
      features = filtered.filter(i => !['resolved', 'closed'].includes(i.status?.toLowerCase()));
      break;

    case 'completed':
      features = filtered.filter(i => ['resolved', 'closed'].includes(i.status?.toLowerCase()));
      break;

    default:
      features = filtered;
      break;
  }

  return {
    type: 'FeatureCollection',
    features: features.map(i => ({
      type: 'Feature',
      properties: {
        id: i.id,
        title: i.title || 'Untitled Incident',
        type: 'incident',
        status: i.status || 'Unknown',
        assignees: i.assignees || [],
        reportedDateTime: i.reportedDateTime || null,
        severity: i.severity || 'Normal',
      },
      geometry: { type: 'Point', coordinates: [i.geoloc.long, i.geoloc.lat] },
    })),
  };
};

export const createResponderGeoJSON = (users, filter) => {
  const filtered = users.filter(
    u => u?.permission?.includes('MobileUnit') && u?.lastKnownLocation?.lon && u?.lastKnownLocation?.lat
  );

  let features;

  switch (filter) {
    case 'activated':
      features = filtered.filter(u => ['unavailable'].includes(u.presence?.toLowerCase()));
      break;

    case 'available':
      features = filtered.filter(u => ['available', 'online'].includes(u.presence?.toLowerCase()));
      break;

    case 'offline':
      features = filtered.filter(u => ['offline', 'disconnected'].includes(u.presence?.toLowerCase()));
      break;

    default:
      features = filtered;
      break;
  }

  return {
    type: 'FeatureCollection',
    features: features.map(u => ({
      type: 'Feature',
      properties: {
        id: u.id,
        displayName: u.displayName || u.name || u.id,
        type: 'responder',
        status: u.status || 'Active',
      },
      geometry: {
        type: 'Point',
        coordinates: [u.lastKnownLocation.lon, u.lastKnownLocation.lat],
      },
    })),
  };
};

export function createCirclePolygon(center, radius, options = {}) {
  const { units = 'kilometers', steps = 64 } = options;

  const [lon, lat] = center;
  const coords = [];
  const earthRadius = units === 'meters' ? 6371000 : 6371; // meters or kilometers

  // Angular distance in radians
  const angularDistance = radius / earthRadius;

  for (let i = 0; i <= steps; i++) {
    const bearing = (i * 360) / steps;
    const point = destinationPoint(lat, lon, bearing, angularDistance);
    coords.push(point);
  }

  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [coords],
    },
    properties: {},
  };
}

function destinationPoint(lat, lon, bearing, angularDistance) {
  const φ1 = (lat * Math.PI) / 180;
  const λ1 = (lon * Math.PI) / 180;
  const θ = (bearing * Math.PI) / 180;

  const φ2 = Math.asin(
    Math.sin(φ1) * Math.cos(angularDistance) + Math.cos(φ1) * Math.sin(angularDistance) * Math.cos(θ)
  );

  const λ2 =
    λ1 +
    Math.atan2(
      Math.sin(θ) * Math.sin(angularDistance) * Math.cos(φ1),
      Math.cos(angularDistance) - Math.sin(φ1) * Math.sin(φ2)
    );

  return [(λ2 * 180) / Math.PI, (φ2 * 180) / Math.PI];
}

export function getDistanceInMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

export const getPresenceColor = presence => {
  const statusColors = constants.RESPONDERS.LIST.STATUS_COLORS;

  if (presence === constants.RESPONDERS.LIST.PRESENCE_STATUS.ONLINE) {
    return statusColors.ONLINE;
  } else if (presence === constants.RESPONDERS.LIST.PRESENCE_STATUS.OFFLINE) {
    return statusColors.OFFLINE;
  }
  return statusColors.DISCONNECTED;
};

export const translatedTitle = title => {
  const { t } = useTranslation();

  const [number, data] = title.split(' - ');

  const translatedText = t('INCIDENT_TYPES', { returnObjects: true }).find(item => item.value === data)?.label;

  return translatedText ? `${number} - ${translatedText}` : title;
};

export const isMobileUnitUser = (id, users) => {
  if (!id || !Array.isArray(users)) return false;

  const user = users.find(u => u?.id?.split('@')[0] === id);
  return user?.permission === 'MobileUnit';
};

// Helper function to get stored sizes
export const getStoredSizes = () => {
  try {
    const stored = sessionStorage.getItem(constants.SIZES_SESSION_KEY);
    if (!stored) return constants.DASHBOARD.DEFAULT_SIZES;
    const parsed = JSON.parse(stored);
    const isValid = parsed.leftSidebarPan && parsed.rightSidebarPan && typeof parsed.leftSidebarPan.value === 'number';

    if (!isValid) {
      console.warn('Invalid stored sizes detected, resetting to defaults.');
      sessionStorage.removeItem(constants.SIZES_SESSION_KEY);
      return constants.DASHBOARD.DEFAULT_SIZES;
    }

    return parsed;
  } catch (e) {
    console.error('Failed to parse stored sizes:', e);
    return constants.DASHBOARD.DEFAULT_SIZES;
  }
};
